import { Host, h } from "@stencil/core";
import { defaultCodeRender } from "./parsers/code-render.lit";
import { LAST_NESTED_CHILD_CLASS, markdownToJSX } from "./parsers/markdown-to-template-result.lit";
import { markdownViewerRenderDictionary, renderDefinedValues } from "./parsers/renders.lit";
// Side effect to define the ch-markdown-viewer-lit
import "./internal/markdown-viewer.lit";
import { markdownViewerExtension } from "./parsers/math";
/**
 * A control to render markdown syntax. It supports GitHub Flavored Markdown
 * (GFM) and code highlighting.
 *  - It parses the incoming markdown to [mdast](https://github.com/syntax-tree/mdast) using [micromark](https://github.com/micromark/micromark) via [mdast-util-from-markdown](https://github.com/syntax-tree/mdast-util-from-markdown).
 *
 * - After that, it implements a reactivity layer by implementing its own render for the mdast. With this, changes to the input markdown only update the portion of the DOM that changes.
 *
 * - It supports Github Flavored Markdown (GFM) by using [mdast-util-gfm](https://github.com/syntax-tree/mdast-util-gfm) and [micromark-extension-gfm](https://github.com/micromark/micromark-extension-gfm).
 *
 * - It supports code highlight by parsing the incomming code of the markdown to [hast](https://github.com/micromark/micromark-extension-gfm) using [lowlight](lowlight). After that, it implements a reactivity layer by implementing its own render for the hast.
 *
 * - It also supports all programming languages from [highlight.js](https://github.com/highlightjs/highlight.js).
 *
 * - When the code highlighting is needed at runtime, the control will load on demand the code parser and the programming language needed to parse the code.
 */
export class ChMarkdownViewer {
    constructor() {
        this.avoidFlashOfUnstyledContent = false;
        this.extensions = undefined;
        this.rawHtml = false;
        this.renderCode = undefined;
        this.showIndicator = false;
        this.theme = "ch-markdown-viewer";
        this.value = undefined;
    }
    #templateResult;
    #renders;
    el;
    extensionsChanged() {
        this.#mergeCustomRendersInASingleObject();
    }
    /**
     * Converts markdown abstract syntax tree (mdast) into TemplateResult`.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #renderChildren = async (parent, metadata, functions) => {
        const childrenLength = parent.children.length;
        const asyncTemplateResult = new Array(childrenLength);
        // Get the async TemplateResult
        for (let index = 0; index < childrenLength; index++) {
            const child = parent.children[index];
            const render = this.#renders[child.type];
            if (render) {
                asyncTemplateResult.push(render(child, metadata, functions));
            }
        }
        // Wait for all results to be completed in parallel
        const renderedContent = await Promise.allSettled(asyncTemplateResult);
        // Return the Template array. TODO: Avoid additional array generation
        return renderDefinedValues(renderedContent);
    };
    // TODO: In Chameleon 7 this method won't be necessary, since the
    // markdown-parser will be built-in and we will have direct access to it for
    // adding more extensions.
    #getExtensions = () => this.extensions
        ? [markdownViewerExtension, ...this.extensions]
        : [markdownViewerExtension];
    #mergeCustomRendersInASingleObject = () => {
        // Merge the render of the extensions into a single render object
        this.#renders = Object.assign(markdownViewerRenderDictionary, ...this.#getExtensions().map(({ mdastRender }) => mdastRender));
    };
    connectedCallback() {
        this.#mergeCustomRendersInASingleObject();
    }
    async componentWillRender() {
        if (!this.value) {
            return;
        }
        // Don't crash the entire markdown viewer if something goes wrong
        try {
            this.#templateResult = await markdownToJSX(this.value, {
                allowDangerousHtml: true, // Allow dangerous in this version
                codeRender: this.renderCode ?? defaultCodeRender,
                lastNestedChildClass: LAST_NESTED_CHILD_CLASS,
                rawHTML: this.rawHtml,
                showIndicator: this.showIndicator
            }, this.#getExtensions(), this.#renderChildren);
        }
        catch (error) {
            console.error("An error occurred while rendering the following value in the ch-markdown-viewer (this error won't update the current rendered value, it is only a console.error):\n", this.value, "\n\n", error);
        }
    }
    render() {
        if (!this.value) {
            return "";
        }
        return (h(Host, { class: this.showIndicator ? "ch-markdown-viewer-show-indicator" : undefined }, this.theme && (h("ch-theme", { key: "theme", avoidFlashOfUnstyledContent: this.avoidFlashOfUnstyledContent, model: this.theme })), h("ch-markdown-viewer-lit", { value: this.#templateResult })));
    }
    static get is() { return "ch-markdown-viewer"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["markdown-viewer.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["markdown-viewer.css"]
        };
    }
    static get properties() {
        return {
            "avoidFlashOfUnstyledContent": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to visually hide the contents of the root node while the control's\nstyle is not loaded. Only works if the `theme` property is set."
                },
                "attribute": "avoid-flash-of-unstyled-content",
                "reflect": false,
                "defaultValue": "false"
            },
            "extensions": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MarkdownViewerExtension<object>[] | undefined",
                    "resolved": "MarkdownViewerExtension<object>[]",
                    "references": {
                        "MarkdownViewerExtension": {
                            "location": "import",
                            "path": "./parsers/types",
                            "id": "src/components/markdown-viewer/parsers/types.ts::MarkdownViewerExtension"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "example",
                            "text": "```ts\n<p>Some text <button type=\"button\""
                        }, {
                            "name": "click",
                            "text": "=${doSomething}>Value</button></p>\n```"
                        }],
                    "text": "Specifies an array of custom extensions to extend and customize the\nrendered markdown language.\nThere a 3 things needed to implement an extension:\n - A tokenizer (the heavy part of the extension).\n - A mapping between the custom token to the custom mdast nodes (pretty straightforward).\n - A render of the custom mdast nodes in Lit's `TemplateResult` (pretty straightforward).\n\nYou can see an [example here](./examples/index.ts), which turns syntax like\n`Some text [[ Value ]]` to:"
                }
            },
            "rawHtml": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render raw HTML with sanitization."
                },
                "attribute": "raw-html",
                "reflect": false,
                "defaultValue": "false"
            },
            "renderCode": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MarkdownViewerCodeRender | undefined",
                    "resolved": "(options: MarkdownViewerCodeRenderOptions) => TemplateResult",
                    "references": {
                        "MarkdownViewerCodeRender": {
                            "location": "import",
                            "path": "./parsers/types",
                            "id": "src/components/markdown-viewer/parsers/types.ts::MarkdownViewerCodeRender"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property allows us to implement custom rendering for the code blocks."
                }
            },
            "showIndicator": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if an indicator is displayed in the last element rendered.\nUseful for streaming scenarios where a loading indicator is needed."
                },
                "attribute": "show-indicator",
                "reflect": false,
                "defaultValue": "false"
            },
            "theme": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the theme to be used for rendering the control.\nIf `undefined`, no theme will be applied."
                },
                "attribute": "theme",
                "reflect": false,
                "defaultValue": "\"ch-markdown-viewer\""
            },
            "value": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the markdown string to parse."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "extensions",
                "methodName": "extensionsChanged"
            }];
    }
}
// // The last element have children. We must check its sub children
// if ((lastChild as MdAstParent).children !== undefined) {
//   return (lastChild as MdAstParent).children.length > 0
//     ? findLastNestedChild(lastChild as MdAstParent)
//     : lastChild;
// }
